import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Calendar, Clock, CheckCircle, AlertCircle, Users, X, Eye, Phone, Zap, MessageSquare, Wallet, User, Plus, FileText, Bot
} from 'lucide-react';
import { getImageUrl } from '../utils/imageUtils';

const ClassSizeInput = ({ unitId, minSize, onUpdate }) => {
  const [val, setVal] = useState(minSize);

  useEffect(() => {
    setVal(minSize);
  }, [minSize]);

  const handleBlur = () => {
    const num = parseInt(val);
    if (!isNaN(num) && num > 0 && num !== minSize) {
      onUpdate(unitId, num);
    }
  };

  return (
    <input
      type="number"
      min="1"
      className="w-16 p-1 text-right text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        }
      }}
    />
  );
};

const LecturerUnits = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignedUnits, setAssignedUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [studentsForUnit, setStudentsForUnit] = useState([]);
  const [studentsModalVisible, setStudentsModalVisible] = useState(false);
  const [fetchingStudents, setFetchingStudents] = useState(false);

  // Decline modal state
  const [declineModalVisible, setDeclineModalVisible] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [selectedUnitForDecline, setSelectedUnitForDecline] = useState(null);
  const [submittingDecline, setSubmittingDecline] = useState(false);

  // Curriculum Management State
  const [curriculumModalVisible, setCurriculumModalVisible] = useState(false);
  const [curriculumItems, setCurriculumItems] = useState([]);
  const [selectedUnitForCurriculum, setSelectedUnitForCurriculum] = useState(null);
  const [fetchingCurriculum, setFetchingCurriculum] = useState(false);
  const [savingCurriculum, setSavingCurriculum] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [uploadingCurriculum, setUploadingCurriculum] = useState(false);
  const [suggestedPlan, setSuggestedPlan] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [explainingId, setExplainingId] = useState(null);
  const [generatingQuizId, setGeneratingQuizId] = useState(null);

  // Lesson Content Viewer / Editor State
  const [lessonContentModal, setLessonContentModal] = useState(null); // { item }
  const [editingContent, setEditingContent] = useState('');
  const [editMode, setEditMode] = useState(false); // false = view, true = edit
  const [refinementPrompt, setRefinementPrompt] = useState('');
  const [refiningContent, setRefiningContent] = useState(false);
  const [savingContent, setSavingContent] = useState(false);

  useEffect(() => {
    fetchAssignedUnits();
  }, []);

  const fetchAssignedUnits = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/${user.id}/assigned-units`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch assigned units');
      }

      const data = await response.json();
      setAssignedUnits(data);
    } catch (error) {
      console.error('Error fetching assigned units:', error);
      setError('Failed to load assigned units. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ... (Keep existing handlers) ...

  const handleUpdateThreshold = async (unitId, newSize) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/update-threshold`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ unitId, minClassSize: newSize })
      });

      if (response.ok) {
        fetchAssignedUnits(); // Refresh to show new settings
      } else {
        alert('Failed to update class size');
      }
    } catch (err) {
      console.error('Error updating threshold:', err);
    }
  };

  const handleStartClass = async (unitId) => {
    if (!window.confirm('Are you sure you want to start this class? This will notify all enrolled students.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/start-class`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ unitId })
      });

      if (response.ok) {
        alert('Class started successfully!');
        fetchAssignedUnits();
      } else {
        alert('Failed to start class');
      }
    } catch (err) {
      console.error('Error starting class:', err);
    }
  };

  const handleManageContent = (unit) => {
    // Navigate to content management for this unit
    navigate(`/learning-materials/${unit.id}`, { state: { unit } });
  };

  const handleEndClass = async (unitId) => {
    if (!window.confirm('Are you sure you want to end this class? This will mark all current students as completed and reset the pool for new enrollments.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/end-class`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ unitId })
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        fetchAssignedUnits(); // Refresh to show reset status
      } else {
        const err = await response.json();
        alert(err.message || 'Failed to end class');
      }
    } catch (err) {
      console.error('Error ending class:', err);
      alert('Network error while ending class');
    }
  };

  const fetchCurriculum = async (unitId) => {
    try {
      setFetchingCurriculum(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${unitId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        setCurriculumItems(await response.json());
      }
    } catch (err) {
      console.error('Error fetching curriculum:', err);
    } finally {
      setFetchingCurriculum(false);
    }
  };

  const handleUploadCurriculum = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('curriculum', file);
    formData.append('unitId', selectedUnitForCurriculum.id);

    try {
      setUploadingCurriculum(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/curriculum/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        alert('Curriculum uploaded and analyzed successfully!');
        if (selectedUnitForCurriculum) {
          fetchCurriculum(selectedUnitForCurriculum.id);
        }
      } else {
        const err = await response.json();
        alert(err.message || 'Failed to upload curriculum');
      }
    } catch (err) {
      console.error('Error uploading curriculum:', err);
      alert('Network error while uploading curriculum');
    } finally {
      setUploadingCurriculum(false);
      // Reset input
      if (e.target) e.target.value = '';
    }
  };

  const handleAICurriculum = async (unitId) => {
    if (!aiPrompt || aiPrompt.length < 5) {
      alert("Please provide a valid topic prompt.");
      return;
    }

    try {
      setGeneratingAI(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/curriculum/generate-from-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ unitId, prompt: aiPrompt })
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        setAiPrompt('');
        fetchCurriculum(unitId);
      } else {
        const err = await response.json();
        alert(err.message || 'Failed to generate AI course');
      }
    } catch (err) {
      console.error(err);
      alert('Error generating AI course');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleApplyAIPlan = async () => {
    if (!suggestedPlan || !suggestedPlan.steps) return;

    try {
      setSavingCurriculum(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/lesson-plan/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          unitId: selectedUnitForCurriculum.id,
          plan: suggestedPlan.steps
        })
      });

      if (response.ok) {
        alert('Lesson plan applied successfully!');
        setSuggestedPlan(null);
        fetchCurriculum(selectedUnitForCurriculum.id);
      } else {
        alert('Failed to apply plan');
      }
    } catch (err) {
      console.error(err);
      alert('Error applying plan');
    } finally {
      setSavingCurriculum(false);
    }
  };

  const handleAIExplain = async (lessonItemId) => {
    try {
      setExplainingId(lessonItemId);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/lesson/explain`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ lessonItemId })
      });

      if (response.ok) {
        const data = await response.json();
        // Now get Polly audio for this explanation
        const pollyRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/speech/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ text: data.explanation })
        });

        if (pollyRes.ok) {
          const blob = await pollyRes.blob();
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          audio.onended = () => setExplainingId(null);
          audio.play();
        } else {
          setExplainingId(null);
        }
      } else {
        setExplainingId(null);
      }
    } catch (err) {
      console.error(err);
      setExplainingId(null);
    }
  };

  const handleGenerateAIQuiz = async (lessonItemId) => {
    if (!window.confirm('Generate an AI Quiz for this lesson? This will create 5 questions based on your curriculum.')) return;

    try {
      setGeneratingQuizId(lessonItemId);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/quiz/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ lessonItemId })
      });

      if (response.ok) {
        alert('AI Quiz generated and attached successfully!');
        const unitId = selectedUnitForCurriculum.id;
        fetchCurriculum(unitId); // Refresh to show the new quiz
      } else {
        alert('Failed to generate AI Quiz');
      }
    } catch (err) {
      console.error(err);
      alert('Error generating AI Quiz');
    } finally {
      setGeneratingQuizId(null);
    }
  };

  const handleAddCurriculumItem = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newItem = {
      unitId: selectedUnitForCurriculum.id,
      title: formData.get('title'),
      description: formData.get('description'),
      type: formData.get('type'),
      plannedDate: formData.get('plannedDate') || null,
      order: curriculumItems.length + 1
    };

    try {
      setSavingCurriculum(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newItem)
      });

      if (response.ok) {
        e.target.reset();
        fetchCurriculum(selectedUnitForCurriculum.id);
      }
    } catch (err) {
      console.error('Error adding curriculum item:', err);
    } finally {
      setSavingCurriculum(false);
    }
  };

  const handleDeleteCurriculumItem = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) fetchCurriculum(selectedUnitForCurriculum.id);
    } catch (err) {
      console.error('Error deleting item:', err);
    }
  };

  const handleToggleCompletion = async (itemId, isCompleted) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${itemId}/toggle-completion`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isCompleted })
      });
      if (response.ok) {
        fetchCurriculum(selectedUnitForCurriculum.id);
      }
    } catch (err) {
      console.error('Error toggling completion:', err);
    }
  };

  const handleStartLesson = async (itemId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${itemId}/start-lesson`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 400 && errorData.currentStatus) {
          alert(`Cannot start lesson: ${errorData.message}\n\nCurrent class status: ${errorData.currentStatus}\n\nPlease start the class first using the "Start Class" button.`);
        } else {
          alert(errorData.message || 'Failed to start lesson');
        }
        return;
      }

      fetchCurriculum(selectedUnitForCurriculum.id);
    } catch (err) {
      console.error('Error starting lesson:', err);
      alert('Failed to start lesson. Please try again.');
    }
  };

  const handleEndLesson = async (itemId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${itemId}/end-lesson`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        fetchCurriculum(selectedUnitForCurriculum.id);
      }
    } catch (err) {
      console.error('Error ending lesson:', err);
    }
  };

  const handleViewLessonContent = (item) => {
    setLessonContentModal(item);
    setEditingContent(item.description || '');
    setEditMode(false);
    setRefinementPrompt('');
  };

  const handleSaveContent = async () => {
    if (!lessonContentModal) return;
    try {
      setSavingContent(true);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${lessonContentModal.id}/content`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ description: editingContent })
      });
      if (res.ok) {
        const updated = await res.json();
        setLessonContentModal(updated);
        setEditMode(false);
        fetchCurriculum(selectedUnitForCurriculum.id);
      } else {
        alert('Failed to save content.');
      }
    } catch (err) {
      alert('Network error saving content.');
    } finally {
      setSavingContent(false);
    }
  };

  const handleAIRefineContent = async () => {
    if (!lessonContentModal || !refinementPrompt.trim()) return;
    try {
      setRefiningContent(true);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${lessonContentModal.id}/ai-refine`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ instruction: refinementPrompt })
      });
      if (res.ok) {
        const data = await res.json();
        setEditingContent(data.content);
        setLessonContentModal(data.item);
        setRefinementPrompt('');
        fetchCurriculum(selectedUnitForCurriculum.id);
      } else {
        const err = await res.json();
        alert(err.message || 'AI refinement failed.');
      }
    } catch (err) {
      alert('Network error during AI refinement.');
    } finally {
      setRefiningContent(false);
    }
  };

  const handleFinalizeClass = async (unitId) => {
    if (!window.confirm('Are you sure you want to finalize this class? This will graduate all students.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/finalize-class`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ unitId })
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        fetchAssignedUnits(); // Refresh
      } else {
        const err = await response.json();
        alert(err.message || 'Failed to finalize class');
      }
    } catch (err) {
      console.error('Error finalizing class:', err);
      alert('Network error');
    }
  };

  const renderSelfPacedUnitCard = (unit) => {
    const isForming = unit.currentCohortStatus === 'forming';
    const progress = Math.min(100, Math.round((unit.enrolledStudentsCount / unit.minClassSize) * 100));
    const canStart = unit.enrolledStudentsCount >= unit.minClassSize;

    return (
      <div key={unit.id} className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow bg-white">
        <div className="flex justify-between items-start mb-4">
          <div>
            <span className="text-xs font-semibold tracking-wide text-blue-600 uppercase">Subject Pool</span>
            <h3 className="text-xl font-bold text-gray-900 mt-1">{unit.name}</h3>
            <p className="text-gray-600 text-sm">{unit.course?.name}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${unit.currentCohortStatus === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
            {unit.currentCohortStatus === 'active' ? 'Class Active' : 'Forming Cohort'}
          </span>
        </div>

        {/* Cohort Progress Section */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex justify-between items-end mb-2">
            <div>
              <p className="text-2xl font-bold text-gray-900">{unit.enrolledStudentsCount} <span className="text-sm font-normal text-gray-500">enrolled</span></p>
            </div>
            <div className="text-right">
              <label className="text-xs text-gray-500 block mb-1">Target Class Size</label>
              {isForming ? (
                <ClassSizeInput
                  unitId={unit.id}
                  minSize={unit.minClassSize}
                  onUpdate={handleUpdateThreshold}
                />
              ) : (
                <span className="text-sm font-medium">{unit.minClassSize} students</span>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1">
            <div
              className={`h-2.5 rounded-full ${canStart ? 'bg-green-600' : 'bg-blue-600'}`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 text-right">{progress}% of target reached</p>
        </div>

        {/* Consensus / Voting Dashboard */}
        {
          unit.currentCohortStatus === 'pending_conclusion' && unit.consensus && (
            <div className="mb-4 bg-purple-50 p-4 rounded-lg border border-purple-100">
              <h4 className="font-semibold text-purple-900 flex items-center mb-2">
                <AlertCircle className="w-4 h-4 mr-2" />
                Class Closure Review
              </h4>
              <div className="flex justify-between text-sm mb-3">
                <span className="text-gray-600">Total Enrolled: {unit.enrolledStudentsCount}</span>
                <span className="text-gray-600">Votes Cast: {unit.consensus.total}</span>
              </div>
              <div className="flex gap-2 mb-3">
                <div className="flex-1 bg-white p-2 rounded shadow-sm border border-green-100 text-center">
                  <span className="block text-xl font-bold text-green-600">{unit.consensus.granted}</span>
                  <span className="text-xs text-gray-500">Granted</span>
                </div>
                <div className="flex-1 bg-white p-2 rounded shadow-sm border border-red-100 text-center">
                  <span className="block text-xl font-bold text-red-600">{unit.consensus.rejected}</span>
                  <span className="text-xs text-gray-500">Rejected</span>
                </div>
              </div>

              {unit.consensus.rejected > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-red-800 mb-1">Students Requesting Changes:</p>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {unit.consensus.rejectors ? (
                      unit.consensus.rejectors.map((student, idx) => (
                        <div key={idx} className="bg-white p-2 rounded border border-red-100 flex justify-between items-start text-xs">
                          <div>
                            <span className="font-bold text-gray-800 block">{student.name}</span>
                            <span className="text-gray-600 block italic">"{student.reason}"</span>
                          </div>
                          {student.phoneNumber && (
                            <a
                              href={`https://wa.me/${student.phoneNumber.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noreferrer"
                              className="ml-2 text-green-600 hover:text-green-700 bg-green-50 p-1.5 rounded flex items-center gap-1"
                              title="Contact on WhatsApp"
                            >
                              <Phone className="w-3 h-3" />
                              <span>Chat</span>
                            </a>
                          )}
                        </div>
                      ))
                    ) : (
                      // Fallback for backward compatibility
                      <ul className="text-xs text-gray-600 list-disc list-inside bg-white p-2 rounded">
                        {unit.consensus.reasons.map((reason, idx) => (
                          <li key={idx}>{reason}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              <button
                onClick={() => handleFinalizeClass(unit.id)}
                className="w-full py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors font-medium text-sm"
              >
                Finalize & Graduate Class
              </button>
            </div>
          )
        }

        {/* Actions */}
        <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
          {isForming ? (
            <button
              onClick={() => handleStartClass(unit.id)}
              disabled={!canStart}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${canStart
                ? 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
            >
              <CheckCircle className="w-4 h-4" />
              Start Class
            </button>
          ) : (
            <button
              onClick={() => handleManageContent(unit)}
              className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
            >
              <BookOpen className="w-4 h-4" />
              Manage Class
            </button>
          )}

          {unit.currentCohortStatus === 'active' && (
            <button
              onClick={() => handleEndClass(unit.id)}
              className="px-4 py-2 border border-red-200 text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
              title="End engagement and graduate students"
            >
              <CheckCircle className="w-4 h-4" />
              End Class
            </button>
          )}

          <button
            onClick={() => handleViewStudents(unit)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            <Users className="w-4 h-4" />
            Students
          </button>
        </div>

        <button
          onClick={() => {
            setSelectedUnitForCurriculum(unit);
            fetchCurriculum(unit.id);
            setCurriculumModalVisible(true);
          }}
          className="w-full mt-3 py-2 bg-blue-50 text-blue-700 rounded-lg border border-blue-100 hover:bg-blue-100 transition-all font-medium text-sm flex items-center justify-center gap-2 group"
        >
          <Zap className="w-4 h-4 text-yellow-500 group-hover:scale-110 transition-transform" />
          Manage Curriculum & Lesson Plan
        </button>
      </div >
    );
  };

  const handleAcceptAssignment = async (unitId, campusId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/${user.id}/units/${unitId}/accept-assignment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ campusId }),
      });

      const data = await response.json();
      if (response.ok) {
        alert('Assignment accepted successfully!');
        fetchAssignedUnits();
      } else {
        alert(data.message || 'Failed to accept assignment.');
      }
    } catch (error) {
      console.error('Error accepting assignment:', error);
      alert('Failed to accept assignment. Please try again.');
    }
  };

  const handleDeclineAssignment = (unit) => {
    setSelectedUnitForDecline(unit);
    setDeclineReason('');
    setDeclineModalVisible(true);
  };

  const submitDecline = async () => {
    if (!declineReason.trim()) {
      alert('Please provide a reason for declining the assignment.');
      return;
    }

    if (declineReason.trim().length < 10) {
      alert('Decline reason must be at least 10 characters long.');
      return;
    }

    setSubmittingDecline(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/${user.id}/units/${selectedUnitForDecline.id}/decline-assignment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ declineReason: declineReason.trim() }),
      });

      const data = await response.json();
      if (response.ok) {
        alert('Assignment declined successfully!');
        setDeclineModalVisible(false);
        fetchAssignedUnits();
      } else {
        alert(data.message || 'Failed to decline assignment.');
      }
    } catch (error) {
      console.error('Error declining assignment:', error);
      alert('Failed to decline assignment. Please try again.');
    } finally {
      setSubmittingDecline(false);
    }
  };

  const fetchStudentsForUnit = async (unitId, campusId, isSelfPaced = false) => {
    setFetchingStudents(true);
    try {
      let url = `${process.env.REACT_APP_API_BASE_URL}/student-units/byUnitAndCampus?unitId=${unitId}`;

      if (isSelfPaced) {
        // For self-paced units, pass tutorId instead of campusId
        url += `&tutorId=${user.id}`;
      } else {
        url += `&campusId=${campusId}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch students');
      }

      const data = await response.json();
      setStudentsForUnit(data);
    } catch (error) {
      console.error('Error fetching students:', error);
      alert('Failed to load students for this unit.');
    } finally {
      setFetchingStudents(false);
    }
  };

  const handleViewStudents = (unit) => {
    setSelectedUnit(unit);
    const isSelfPaced = unit.learningMode === 'self_paced' || unit.course?.selfPacedEnabled;
    fetchStudentsForUnit(unit.id, unit.lecturer?.campusId, isSelfPaced);
    setStudentsModalVisible(true);
  };

  const handleCompleteUnitForStudent = async (studentUnitId, studentId, unitId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/student-units/complete-unit/${studentId}/${unitId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ grade: 'N/A' }),
      });

      const data = await response.json();
      if (response.ok) {
        alert('Student unit marked as completed!');
        fetchStudentsForUnit(selectedUnit.id, selectedUnit.lecturer?.campusId);
      } else {
        alert(data.message || 'Failed to complete unit for student.');
      }
    } catch (error) {
      console.error('Error completing unit:', error);
      alert('Failed to complete unit. Please try again.');
    }
  };

  const handleApproveCashPayment = async (studentId, unitId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/payments/approve-cash/${studentId}/${unitId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lecturerId: user.id,
          approvalNotes: 'Approved by lecturer via units page'
        })
      });

      if (response.ok) {
        alert('Cash payment approved successfully!');
        // Refresh students list
        fetchStudentsForUnit(selectedUnit.id, selectedUnit.lecturer?.campusId);
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to approve cash payment');
      }
    } catch (err) {
      console.error('Error approving cash payment:', err);
      alert('Error approving cash payment');
    }
  };

  const handleRejectCashPayment = async (studentId, unitId) => {
    const reason = prompt('Please provide a reason for rejecting this payment:');
    if (!reason || !reason.trim()) {
      alert('Rejection reason is required');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/payments/reject-cash/${studentId}/${unitId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lecturerId: user.id,
          rejectionNotes: reason.trim()
        })
      });

      if (response.ok) {
        alert('Cash payment rejected');
        // Refresh students list
        fetchStudentsForUnit(selectedUnit.id, selectedUnit.lecturer?.campusId);
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to reject cash payment');
      }
    } catch (err) {
      console.error('Error rejecting cash payment:', err);
      alert('Error rejecting cash payment');
    }
  };

  const getUnitStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'pending_acceptance':
        return 'text-yellow-600 bg-yellow-100';
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      case 'upcoming':
        return 'text-purple-600 bg-purple-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'accepted':
        return 'Active';
      case 'pending':
        return 'Waiting for Acceptance';
      case 'active':
        return 'Ongoing';
      case 'pending_acceptance':
        return 'Waiting for Acceptance';
      case 'completed':
        return 'Completed';
      case 'upcoming':
        return 'Upcoming';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-50 border border-error-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-error-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-error-800 mb-2">Error Loading Units</h3>
        <p className="text-error-700 mb-4">{error}</p>
        <button
          onClick={fetchAssignedUnits}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  const getLecturerSummary = () => {
    const totalUnits = assignedUnits.length;
    const pendingUnits = assignedUnits.filter(unit => unit.assignmentStatus === 'pending').length;
    const activeUnits = assignedUnits.filter(unit => unit.assignmentStatus === 'accepted').length;
    const completedUnits = assignedUnits.filter(unit => unit.status === 'completed').length;

    return { totalUnits, pendingUnits, activeUnits, completedUnits };
  };

  const lecturerSummary = getLecturerSummary();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📚 Assigned Units</h1>
          <p className="text-gray-600 mt-1">Manage your teaching assignments</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Units</p>
              <p className="text-2xl font-bold">{lecturerSummary.totalUnits}</p>
            </div>
            <BookOpen className="w-8 h-8 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm">Pending</p>
              <p className="text-2xl font-bold">{lecturerSummary.pendingUnits}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Active</p>
              <p className="text-2xl font-bold">{lecturerSummary.activeUnits}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Completed</p>
              <p className="text-2xl font-bold">{lecturerSummary.completedUnits}</p>
            </div>
            <Users className="w-8 h-8 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Units List */}
      {assignedUnits.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {assignedUnits.map((unit) => {
            if (unit.learningMode === 'self_paced') {
              return renderSelfPacedUnitCard(unit);
            }
            return (
              <div key={unit.id} className="card hover:shadow-large transition-all duration-300">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {unit.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {unit.course?.name || 'Course not assigned'}
                      </p>
                    </div>
                  </div>
                  <span className={`status-badge ${getUnitStatusColor(unit.assignmentStatus || unit.status)}`}>
                    {getStatusText(unit.assignmentStatus || unit.status)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">
                      {unit.enrolledStudentsCount || 0} students
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">
                      {unit.classDate ? new Date(unit.classDate).toLocaleDateString() : 'No date set'}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2">
                  {unit.assignmentStatus === 'pending' && (
                    <>
                      <button
                        onClick={() => handleAcceptAssignment(unit.id, unit.campusId || unit.lecturer?.campusId)}
                        className="btn-success flex-1"
                      >
                        ✓ Accept
                      </button>
                      <button
                        onClick={() => handleDeclineAssignment(unit)}
                        className="btn-error flex-1"
                      >
                        ✗ Decline
                      </button>
                    </>
                  )}

                  {unit.assignmentStatus === 'accepted' && (
                    <button
                      onClick={() => handleViewStudents(unit)}
                      className="btn-primary flex-1"
                    >
                      👁️ View Students
                    </button>
                  )}

                  {unit.status === 'completed' && (
                    <div className="text-center py-2 text-green-600 font-medium">
                      ✓ Completed
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No units assigned</h3>
          <p className="text-gray-600">New assignments will appear here</p>
        </div>
      )}

      {/* Decline Modal */}
      {declineModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Decline Assignment</h3>
              <button
                onClick={() => setDeclineModalVisible(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for declining "{selectedUnitForDecline?.name}"
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={4}
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="Please provide a detailed reason..."
                  maxLength={500}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {declineReason.length}/500 characters (minimum 10)
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setDeclineModalVisible(false)}
                  className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitDecline}
                  disabled={submittingDecline}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {submittingDecline ? 'Submitting...' : 'Submit Decline'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Students Modal */}
      {studentsModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header with Income Stats */}
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    👥 Students for {selectedUnit?.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">Manage enrolled students and track cohort revenue</p>
                </div>
                <button
                  onClick={() => setStudentsModalVisible(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-200 rounded-full"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {!fetchingStudents && (
                <div className="flex gap-4">
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex-1">
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <Users className="w-4 h-4" />
                      <span className="text-sm font-medium">Enrolled Students</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {studentsForUnit.length}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100 shadow-sm flex-1">
                    <div className="flex items-center gap-2 text-blue-800 mb-2">
                      <Wallet className="w-4 h-4" />
                      <span className="text-sm font-medium">Your Expected Income</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-700">
                      KES {(studentsForUnit.reduce((acc, curr) => acc + (parseFloat(curr.paymentAmount) || 0), 0) * 0.60).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 overflow-y-auto">
              {fetchingStudents ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Loading students...</span>
                </div>
              ) : studentsForUnit.length > 0 ? (
                <div className="space-y-4">
                  {studentsForUnit.map((studentUnit) => (
                    <div key={`${studentUnit.studentId}-${studentUnit.unitId}`} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white flex items-start gap-4">
                      {studentUnit.student?.profilePicture ? (
                        <img
                          src={getImageUrl(studentUnit.student.profilePicture)}
                          alt={studentUnit.student.name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center border-2 border-white shadow-sm">
                          <User className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-bold text-gray-900 text-lg">
                                {studentUnit.student?.name || `Student ${studentUnit.studentId}`}
                              </h4>
                              {studentUnit.status === 'completed' && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-bold rounded-full border border-green-200">
                                  COMPLETED
                                </span>
                              )}
                            </div>

                            <div className="flex flex-col gap-1 text-sm text-gray-600 mb-3">
                              <span>{studentUnit.student?.email || `ID: ${studentUnit.studentId}`}</span>
                              {studentUnit.student?.phoneNumber && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {studentUnit.student.phoneNumber}
                                </span>
                              )}
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${studentUnit.paymentStatus === 'confirmed'
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : studentUnit.paymentStatus === 'pending'
                                  ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                  : 'bg-red-50 text-red-700 border-red-200'
                                }`}>
                                {studentUnit.paymentStatus === 'confirmed' ? `Paid: KES ${(parseFloat(studentUnit.paymentAmount) || 0).toLocaleString()}` :
                                  studentUnit.paymentStatus === 'rejected' ? 'Payment Rejected' :
                                    'Payment Pending'}
                              </span>

                              {studentUnit.paymentMethod && (
                                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200 capitalize">
                                  {studentUnit.paymentMethod}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex flex-col items-end gap-2 ml-4">
                            <button
                              onClick={() => alert('Chat integration coming soon!')}
                              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all"
                              title="Chat with student"
                            >
                              <MessageSquare className="w-5 h-5" />
                            </button>

                            {/* Cash Payment Actions */}
                            {studentUnit.paymentMethod === 'cash' && studentUnit.paymentStatus === 'pending' && (
                              <div className="flex gap-2 mt-2">
                                <button
                                  onClick={() => handleApproveCashPayment(studentUnit.studentId, studentUnit.unitId)}
                                  className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700 transition-colors flex items-center gap-1 shadow-sm"
                                >
                                  <CheckCircle className="w-3 h-3" />
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleRejectCashPayment(studentUnit.studentId, studentUnit.unitId)}
                                  className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-red-700 transition-colors flex items-center gap-1 shadow-sm"
                                >
                                  <X className="w-3 h-3" />
                                  Reject
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Payment Notes */}
                        {studentUnit.paymentNotes && (
                          <div className="mt-4 text-xs text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100 italic">
                            "{studentUnit.paymentNotes}"
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-gray-900 font-medium mb-1">No students enrolled yet</h3>
                  <p className="text-sm text-gray-500">Share your unit link to get students started.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Curriculum Modal */}
      {curriculumModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl h-[95vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Unit Content & Lesson Plan</h2>
                <p className="text-sm text-gray-600">{selectedUnitForCurriculum?.name}</p>
              </div>
              <button
                onClick={() => setCurriculumModalVisible(false)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* AI Generation & Data Ingestion Tool */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* PDF Ingestion */}
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-purple-900 flex items-center text-sm">
                      <FileText className="w-3.5 h-3.5 mr-2 text-purple-600" />
                      Ingest Curriculum (PDF)
                    </h4>
                  </div>
                  <p className="text-[10px] text-purple-700 mb-3">
                    Upload your unit syllabus or textbook content for the AI Teacher to learn.
                  </p>
                  <label className={`w-full py-2 flex items-center justify-center gap-2 rounded-lg cursor-pointer transition-all border border-dashed ${uploadingCurriculum ? 'bg-gray-100 border-gray-300' : 'bg-white border-purple-300 hover:bg-purple-100'}`}>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleUploadCurriculum}
                      disabled={uploadingCurriculum}
                      className="hidden"
                    />
                    {uploadingCurriculum ? (
                      <div className="flex items-center gap-2 text-xs font-bold text-gray-500">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500"></div>
                        Uploading...
                      </div>
                    ) : (
                      <span className="text-xs font-bold text-purple-700 flex items-center gap-2">
                        <Plus className="w-3.5 h-3.5" />
                        Upload PDF
                      </span>
                    )}
                  </label>
                </div>

                {/* AI Planner */}
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 shadow-sm flex flex-col h-full">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-blue-900 flex items-center text-sm">
                      <Zap className="w-3.5 h-3.5 mr-2 text-yellow-500" />
                      Dynamic AI Course Builder
                    </h4>
                  </div>
                  <p className="text-[10px] text-blue-700 mb-3">
                    Auto-generate a comprehensive course (lessons, markdown notes, and YouTube materials) organically from a topic prompt.
                  </p>
                  
                  <textarea 
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="E.g. Introduction to Python Data Structures"
                    className="w-full text-xs p-2 mb-2 rounded-lg border border-blue-200 outline-none focus:ring-2 focus:ring-blue-500 flex-1 min-h-[60px]"
                  />
                  <button
                    onClick={() => handleAICurriculum(selectedUnitForCurriculum.id)}
                    disabled={generatingAI || !aiPrompt.trim()}
                    className="w-full text-[10px] bg-blue-600 text-white px-3 py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1 font-bold transition-all"
                  >
                    {generatingAI ? (
                       <span className="flex items-center gap-2">
                         <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                         Generating (up to 2 mins)...
                       </span>
                    ) : '✨ Auto-Generate Full Course'}
                  </button>
                </div>
              </div>

              {/* Add New Item Form */}
              <form onSubmit={handleAddCurriculumItem} className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
                <div className="grid grid-cols-3 gap-3">
                  <input
                    name="title"
                    placeholder="Section Title"
                    required
                    className="p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none col-span-1"
                  />
                  <select
                    name="type"
                    className="p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="curriculum">Curriculum Point</option>
                    <option value="schedule">Schedule Event</option>
                    <option value="lesson_plan">Lesson Goal</option>
                    <option value="outline">Module Outline</option>
                  </select>
                  <input
                    name="plannedDate"
                    type="date"
                    className="p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={savingCurriculum}
                  className="w-full py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors text-sm font-medium"
                >
                  {savingCurriculum ? 'Saving...' : '+ Add Section'}
                </button>
              </form>

              {/* Current Items List */}
              <div className="space-y-3">
                <h4 className="font-bold text-gray-800 text-sm">Course Outline ({curriculumItems.length} steps)</h4>
                {fetchingCurriculum ? (
                  <div className="text-center py-6">Loading...</div>
                ) : (
                  <div className="space-y-4">
                    {curriculumItems.map((item, index) => (
                      <div key={item.id} className={`bg-white border rounded-2xl shadow-sm transition-all overflow-hidden ${
                        item.isLive ? 'border-orange-200 shadow-orange-100/50 shadow-lg' :
                        item.isCompleted ? 'border-green-100' : 'border-gray-200'
                      }`}>
                        
                        {/* Top Header Row */}
                        <div className={`p-4 border-b ${item.isLive ? 'bg-orange-50/30' : item.isCompleted ? 'bg-green-50/10' : 'bg-gray-50/30'} flex items-start gap-4`}>
                          <div className={`flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-md font-black text-[10px] ${
                            item.isLive ? 'bg-orange-600 text-white' :
                            item.isCompleted ? 'bg-green-600 text-white' : 'bg-blue-50 text-blue-600'
                          }`}>
                            {item.isLive ? '🔴' : item.isCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : index + 1}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start gap-4">
                              <h5 className={`font-black text-[13px] leading-tight ${item.isLive ? 'text-orange-900' : item.isCompleted ? 'text-green-900' : 'text-gray-900'}`}>{item.title}</h5>
                              <button onClick={() => handleDeleteCurriculumItem(item.id)} className="text-gray-300 hover:text-red-500 flex-shrink-0">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-1.5">
                              <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">{item.type.replace('_', ' ')}</span>
                              {item.isCompleted && (
                                <span className="text-[9px] text-green-600 font-bold bg-green-50 px-2 py-0.5 rounded-full">Done: {new Date(item.completedAt).toLocaleDateString()}</span>
                              )}
                              {item.isLive && item.liveStartedAt && (
                                <span className="text-[9px] bg-orange-600 text-white px-2 py-0.5 rounded-full font-black uppercase tracking-wide animate-pulse">Live Now</span>
                              )}
                              {item.plannedDate && (
                                <span className="text-[9px] text-gray-400">Target: {new Date(item.plannedDate).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* 3-Column Content Row */}
                        <div className="flex flex-col md:flex-row">
                          {/* Column 1: Actions */}
                          <div className="w-full md:w-56 p-4 border-b md:border-b-0 md:border-r border-gray-100 flex flex-col gap-2 justify-start">
                            {item.isLive ? (
                              <button onClick={() => handleEndLesson(item.id)} className="w-full text-[10px] font-black px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors uppercase tracking-widest">
                                End Lesson
                              </button>
                            ) : (
                              <button
                                onClick={() => handleStartLesson(item.id)}
                                disabled={selectedUnitForCurriculum?.currentCohortStatus !== 'active'}
                                className={`w-full text-[10px] font-black px-4 py-2 rounded-full transition-colors uppercase tracking-widest ${
                                  selectedUnitForCurriculum?.currentCohortStatus === 'active' ? 'bg-gray-800 text-white hover:bg-gray-900' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                }`}
                              >
                                Attend Lesson
                              </button>
                            )}

                            <button onClick={() => handleToggleCompletion(item.id, !item.isCompleted)} className={`w-full text-[10px] font-black px-4 py-2 rounded-full border transition-colors uppercase tracking-widest ${
                              item.isCompleted ? 'bg-green-100 border-green-200 text-green-800 hover:bg-green-200' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                            }`}>
                              {item.isCompleted ? 'Mark Undone' : 'Mark as Done'}
                            </button>

                            <button onClick={() => handleAIExplain(item.id)} disabled={explainingId === item.id} className={`w-full text-[9px] font-bold px-3 py-1.5 rounded-full border flex items-center justify-center gap-1.5 transition-all ${
                              explainingId === item.id ? 'bg-purple-100 text-purple-700 border-purple-300 animate-pulse' : 'bg-white text-indigo-600 border-indigo-200 hover:bg-indigo-50'
                            }`}>
                              <Bot className="w-3 h-3" /> {explainingId === item.id ? 'Explaining...' : 'Explain with AI'}
                            </button>

                            <button onClick={() => handleGenerateAIQuiz(item.id)} disabled={generatingQuizId === item.id} className={`w-full text-[9px] font-bold px-3 py-1.5 rounded-full border flex items-center justify-center gap-1.5 transition-all ${
                              generatingQuizId === item.id ? 'bg-yellow-100 text-yellow-700 border-yellow-300 animate-pulse' : 'bg-white text-green-600 border-green-200 hover:bg-green-50'
                            }`}>
                              <CheckCircle className="w-3 h-3" /> {generatingQuizId === item.id ? 'Generating Quiz...' : 'Generate AI Quiz'}
                            </button>
                          </div>

                          {/* Column 2: Lesson Content Preview */}
                          <div className="flex-1 p-4 border-b md:border-b-0 md:border-r border-gray-100 flex flex-col justify-center">
                            {item.description ? (
                              <button
                                onClick={() => handleViewLessonContent(item)}
                                className="w-full text-left bg-white border border-indigo-100 rounded-xl p-4 hover:shadow-md transition-all group"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-[9px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-1.5">
                                    <BookOpen className="w-3 h-3" /> Lesson Content (AI Generated)
                                  </span>
                                  <span className="text-[9px] text-indigo-400 group-hover:text-indigo-600 font-bold whitespace-nowrap">Click to enlarge &rarr;</span>
                                </div>
                                <p className="text-[11px] text-gray-500 line-clamp-3 leading-relaxed font-medium">
                                  {item.description.replace(/[#*`>]/g, '').slice(0, 180)}...
                                </p>
                              </button>
                            ) : (
                              <div className="h-full flex items-center justify-center p-6 border-2 border-dashed border-gray-100 rounded-xl text-center">
                                <div>
                                  <BookOpen className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">No Lesson Content</p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Column 3: Resources */}
                          <div className="w-full md:w-64 p-4 flex flex-col min-h-full">
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-3">Lesson Resources</p>
                            
                            <div className="flex-1 mb-4">
                              <div className="flex flex-col gap-1.5">
                                {item.assignments?.map(a => (
                                  <div key={`a-${a.id}`} className="flex items-center gap-1.5 text-blue-700 text-[10px] font-bold truncate">
                                    <FileText className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{a.title}</span>
                                  </div>
                                ))}
                                {item.quizzes?.map(q => (
                                  <div key={`q-${q.id}`} className="flex items-center gap-1.5 text-green-700 text-[10px] font-bold truncate">
                                    <CheckCircle className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{q.title}</span>
                                  </div>
                                ))}
                                {item.materials?.map(m => (
                                  <div key={`m-${m.id}`} className="flex items-center gap-1.5 text-purple-700 text-[10px] font-bold truncate">
                                    <BookOpen className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{m.title}</span>
                                  </div>
                                ))}
                                {(!item.assignments?.length && !item.quizzes?.length && !item.materials?.length) && (
                                  <span className="text-[10px] text-gray-400 font-medium italic">No resources attached</span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-1 mt-auto pt-3 border-t border-gray-50 flex-wrap">
                              <button onClick={() => navigate(`/assignments?unitId=${selectedUnitForCurriculum.id}&action=create_assignment&lessonItemId=${item.id}`)} className="flex-1 text-[9px] font-black bg-blue-600 text-white px-0 py-1.5 rounded hover:bg-blue-700 flex justify-center items-center gap-1 transition-colors">
                                <Plus className="w-3 h-3" /> Assignment
                              </button>
                              <button onClick={() => navigate(`/assignments?unitId=${selectedUnitForCurriculum.id}&action=create_quiz&lessonItemId=${item.id}`)} className="flex-1 text-[9px] font-black bg-green-600 text-white px-0 py-1.5 rounded hover:bg-green-700 flex justify-center items-center gap-1 transition-colors">
                                <Plus className="w-3 h-3" /> Quiz
                              </button>
                              <button onClick={() => navigate(`/learning-materials?unitId=${selectedUnitForCurriculum.id}&action=upload&lessonItemId=${item.id}`)} className="flex-1 text-[9px] font-black bg-purple-600 text-white px-0 py-1.5 rounded hover:bg-purple-700 flex justify-center items-center gap-1 transition-colors">
                                <Plus className="w-3 h-3" /> Material
                              </button>
                            </div>
                          </div>
                        </div>

                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-gray-100 bg-gray-50 text-right">
                <button
                  onClick={() => setCurriculumModalVisible(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lesson Content Viewer / Editor Modal */}
      {lessonContentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4" onClick={() => setLessonContentModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl h-[95vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-start justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-indigo-600 to-purple-700">
              <div className="flex-1 min-w-0">
                <span className="text-[10px] font-bold text-indigo-200 uppercase tracking-widest">AI-Generated Lesson Notes</span>
                <h3 className="text-white font-black text-lg mt-0.5 leading-tight truncate">{lessonContentModal.title}</h3>
              </div>
              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                <button
                  onClick={() => { setEditMode(!editMode); if (!editMode) setEditingContent(lessonContentModal.description || ''); }}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${
                    editMode ? 'bg-white text-indigo-700' : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  {editMode ? '👁 View' : '✏️ Edit'}
                </button>
                <button onClick={() => setLessonContentModal(null)} className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors">
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6">
              {editMode ? (
                <textarea
                  value={editingContent}
                  onChange={e => setEditingContent(e.target.value)}
                  className="w-full h-64 text-sm text-gray-800 font-mono bg-gray-50 border border-gray-200 rounded-xl p-4 resize-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none leading-relaxed"
                  placeholder="Write or paste lesson markdown content here..."
                />
              ) : (
                <div className="prose-sm max-w-none text-gray-800 space-y-1">
                  {(lessonContentModal.description || '').split('\n').map((line, i) => {
                    if (line.startsWith('### ')) return <h3 key={i} className="text-sm font-bold text-gray-800 mt-4 mb-1">{line.slice(4)}</h3>;
                    if (line.startsWith('## ')) return <h2 key={i} className="text-base font-extrabold text-indigo-900 mt-5 mb-2 pb-1 border-b border-indigo-100">{line.slice(3)}</h2>;
                    if (line.startsWith('# ')) return <h1 key={i} className="text-lg font-black text-indigo-900 mt-4 mb-3">{line.slice(2)}</h1>;
                    if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} className="ml-4 text-sm text-gray-700 mb-0.5 list-disc">{line.slice(2)}</li>;
                    if (/^\d+\. /.test(line)) return <li key={i} className="ml-4 text-sm text-gray-700 mb-0.5 list-decimal">{line.replace(/^\d+\. /, '')}</li>;
                    if (line === '---') return <hr key={i} className="border-gray-200 my-3" />;
                    if (line.trim() === '') return <div key={i} className="h-1.5" />;
                    return <p key={i} className="text-sm text-gray-700 mb-1 leading-relaxed">{line.replace(/\*\*(.+?)\*\*/g, '$1')}</p>;
                  })}
                </div>
              )}

              {/* AI Refine Panel */}
              <div className="mt-6 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-100 p-4">
                <h4 className="text-xs font-black text-purple-800 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <Bot className="w-3.5 h-3.5" />
                  Refine with AI
                </h4>
                <p className="text-[10px] text-purple-600 mb-3">Give an instruction to the AI to rewrite or improve this lesson content. Changes are saved automatically.</p>
                <div className="flex gap-2">
                  <input
                    value={refinementPrompt}
                    onChange={e => setRefinementPrompt(e.target.value)}
                    placeholder='E.g. "Make it more concise" or "Add more practical examples"'
                    className="flex-1 text-xs border border-purple-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-400 outline-none bg-white"
                    onKeyDown={e => e.key === 'Enter' && !refiningContent && handleAIRefineContent()}
                  />
                  <button
                    onClick={handleAIRefineContent}
                    disabled={refiningContent || !refinementPrompt.trim()}
                    className="text-xs font-bold px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors whitespace-nowrap"
                  >
                    {refiningContent ? (
                      <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Refining...</>
                    ) : (
                      <><Bot className="w-3 h-3" /> Refine</>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
              <p className="text-[10px] text-gray-400">
                {lessonContentModal.description ? `${lessonContentModal.description.length.toLocaleString()} characters` : 'No content yet'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setLessonContentModal(null)}
                  className="text-sm font-medium px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Close
                </button>
                {editMode && (
                  <button
                    onClick={handleSaveContent}
                    disabled={savingContent}
                    className="text-sm font-bold px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2 transition-colors"
                  >
                    {savingContent ? <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</> : '💾 Save Changes'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LecturerUnits;
